/**
 * xx network keyring.
 *
 * Manages user accounts (keypairs) with encrypted storage in localStorage.
 * Private keys are NEVER stored unencrypted — they are decrypted only when
 * needed to sign a transaction, and re-encrypted immediately after.
 *
 * This implementation uses standard sr25519 accounts.
 *
 * IMPORTANT: The encryption here relies on the user's password + browser's
 * localStorage. A hardened deployment would consider the WebCrypto API with
 * a proper key derivation function and potentially the device's secure
 * storage (e.g., via Credential Management API where available).
 *
 * SCRYPT COMPATIBILITY NOTE:
 * The official wallet.xx.network exports v3 keystore JSON with scrypt N=131072
 * (stronger than Polkadot's hardcoded default of N=32768). The @polkadot/keyring
 * library refuses to decrypt any scrypt JSON that doesn't match its exact default
 * params, throwing "Invalid injected scrypt params found". To interoperate with
 * the official wallet, we implement a manual decryption path using scrypt-js +
 * tweetnacl that honours whatever scrypt params are embedded in the JSON.
 */

import { Keyring } from '@polkadot/keyring';
import type { KeyringPair, KeyringPair$Json } from '@polkadot/keyring/types';
import {
  mnemonicGenerate,
  mnemonicValidate,
  cryptoWaitReady,
  base64Decode,
  base64Encode,
} from '@polkadot/util-crypto';
import { stringToU8a } from '@polkadot/util';
import { scrypt as scryptAsync } from 'scrypt-js';
import nacl from 'tweetnacl';
import { XX_GENESIS_HASH, XX_SS58_PREFIX } from '../api/constants';

const STORAGE_KEY = 'xx-wallet:accounts';

// Byte layout of the encoded field when scrypt is used (v3 format):
//   [0..32]   scrypt salt (32 bytes)
//   [32..36]  N  (uint32 little-endian)
//   [36..40]  p  (uint32 little-endian)
//   [40..44]  r  (uint32 little-endian)
//   [44..68]  nacl secretbox nonce (24 bytes)
//   [68..]    nacl secretbox ciphertext
const SCRYPT_SALT_LEN = 32;
const SCRYPT_HEADER_LEN = 44; // salt + N + p + r
const NACL_NONCE_LEN = 24;
const NACL_ENCRYPTED_HEADER_LEN = SCRYPT_HEADER_LEN + NACL_NONCE_LEN; // 68

// Strong scrypt params used when this wallet creates a new account.
// Matches what wallet.xx.network exports (and exceeds @polkadot/keyring's
// default of N=32768) so a JSON exported from this wallet has the same
// brute-force resistance as one exported from the official desktop wallet.
const STRONG_SCRYPT_N = 131072;
const STRONG_SCRYPT_R = 8;
const STRONG_SCRYPT_P = 1;

// PKCS8 wrapper markers that surround the secret key in the decrypted payload.
// These come from @polkadot/keyring/pair/defaults.
const PKCS8_HEADER = new Uint8Array([
  48, 83, 2, 1, 1, 48, 5, 6, 3, 43, 101, 112, 4, 34, 4, 32,
]);
const PKCS8_DIVIDER = new Uint8Array([161, 35, 3, 33, 0]);

/**
 * A password-protected account whose encrypted keystore lives in this
 * wallet. This is the original (and default) account shape — records
 * persisted before the `source` discriminator existed have no `source`
 * field and parse as local accounts without migration.
 */
export interface LocalAccount {
  /** Discriminator. Absent on legacy records — absent means 'local'. */
  source?: 'local';
  /** SS58-encoded address (format 55 — starts with "6"). */
  address: string;
  /** User-provided display name. */
  name: string;
  /** Polkadot JSON keystore — encrypted with the user's password. */
  json: KeyringPair$Json;
  /** When the account was created or imported. */
  createdAt: number;
}

/**
 * An account whose private key lives on a Ledger device and NEVER enters
 * the browser. The wallet stores only the address + derivation slots;
 * there is no keystore, no password, nothing to export or back up from
 * this wallet's side. Signing ships the payload to the device over
 * WebHID and the user confirms on the device screen.
 */
export interface LedgerAccount {
  source: 'ledger';
  /** SS58-encoded address (format 55), as confirmed on the device. */
  address: string;
  /** User-provided display name. */
  name: string;
  /** BIP44 slots for m/44'/1955'/account'/change'/index' (all hardened). */
  ledger: {
    account: number;
    change: number;
    index: number;
  };
  /** When the account was added to this wallet. */
  createdAt: number;
}

export type StoredAccount = LocalAccount | LedgerAccount;

/** Type guard: account signs on a Ledger device (no local keystore). */
export function isLedgerAccount(a: StoredAccount): a is LedgerAccount {
  return a.source === 'ledger';
}

/** Type guard: account has a local password-encrypted keystore. */
export function isLocalAccount(a: StoredAccount): a is LocalAccount {
  return a.source !== 'ledger';
}

/** Options for creating a new account. */
export interface CreateAccountOptions {
  name: string;
  password: string;
  /** 12 or 24. 24 is recommended by xx network. */
  wordCount?: 12 | 24;
}

/** Options for importing from a mnemonic. */
export interface ImportFromMnemonicOptions {
  name: string;
  mnemonic: string;
  password: string;
}

/** Options for importing from a JSON keystore. */
export interface ImportFromJsonOptions {
  name?: string;
  json: KeyringPair$Json;
  /** Password to validate the JSON can be decrypted. */
  password: string;
}

/**
 * Manually decrypt a Polkadot v3 JSON keystore using whatever scrypt params
 * are embedded in the file. Bypasses the @polkadot/util-crypto hardcoded-params
 * assertion so we can load JSON exported by wallet.xx.network (which uses
 * N=131072 instead of the library default of N=32768).
 *
 * Returns the raw decrypted PKCS8 payload on success, throws on wrong password.
 *
 * Exported for unit testing — callers in this file use it via the public
 * `importFromJson` and `unlock` methods on the keyring class.
 */
export async function manualScryptDecrypt(
  json: KeyringPair$Json,
  password: string
): Promise<Uint8Array> {
  // Pin the keystore format version + cipher suite. We only know the byte
  // layout for v3 scrypt + xsalsa20-poly1305 keystores. A v2 (or future v4)
  // would have a different layout, and feeding it through this function
  // would silently slice bytes from the wrong offsets — bounds checks
  // would either reject (good) or worse, pass and then fail at the
  // secretbox open step. Either way, fail explicitly here instead.
  const enc = json.encoding;
  if (!enc || enc.version !== '3') {
    throw new Error(
      `Unsupported keystore version (expected 3, got ${enc?.version ?? 'unknown'}).`
    );
  }
  if (
    !Array.isArray(enc.type) ||
    !enc.type.includes('scrypt') ||
    !enc.type.includes('xsalsa20-poly1305')
  ) {
    throw new Error(
      'Keystore is not encrypted with the expected scrypt + xsalsa20-poly1305 suite.'
    );
  }

  const encoded = base64Decode(json.encoded);

  // Parse scrypt params from the header
  const salt = encoded.slice(0, SCRYPT_SALT_LEN);
  const view = new DataView(encoded.buffer, encoded.byteOffset);
  const N = view.getUint32(SCRYPT_SALT_LEN, true);
  const p = view.getUint32(SCRYPT_SALT_LEN + 4, true);
  const r = view.getUint32(SCRYPT_SALT_LEN + 8, true);

  // Sanity bounds on attacker-supplied scrypt params. The dominant risk is
  // MEMORY, not CPU: scrypt's working set is ~128 * N * r bytes, so an
  // unbounded N (or r) lets a crafted keystore allocate gigabytes and
  // OOM-crash the tab. These ceilings cap the worst case at ~256 MiB
  // (128 * 262144 * 8) while still accepting every real export — the
  // official wallet.xx.network uses N=131072, r=8, p=1, and Polkadot
  // defaults to N=32768, r=8, p=1. p is capped too, since it multiplies CPU.
  if (N < 1024 || N > 262144 || p < 1 || p > 4 || r < 1 || r > 8) {
    throw new Error('Keystore has out-of-range scrypt params.');
  }
  if ((N & (N - 1)) !== 0) {
    throw new Error('Keystore scrypt N is not a power of two.');
  }

  // Derive the 64-byte key using scrypt (pure-JS implementation, no WASM)
  const passwordBytes = stringToU8a(password);
  const derivedKey = await scryptAsync(passwordBytes, salt, N, r, p, 64);
  // Polkadot only uses the first 32 bytes as the secretbox key
  const secretBoxKey = derivedKey.slice(0, 32);

  // The rest of the encoded blob is nacl.secretbox(nonce || ciphertext)
  const nonce = encoded.slice(SCRYPT_HEADER_LEN, SCRYPT_HEADER_LEN + NACL_NONCE_LEN);
  const ciphertext = encoded.slice(NACL_ENCRYPTED_HEADER_LEN);

  const decrypted = nacl.secretbox.open(ciphertext, nonce, secretBoxKey);
  if (!decrypted) {
    throw new Error('Incorrect password for this keystore.');
  }
  return decrypted;
}

/**
 * Encrypt a PKCS8-wrapped sr25519 keypair into a Polkadot v3 JSON keystore
 * using the strong xx-network-style scrypt parameters (N=131072, r=8, p=1).
 *
 * This is the inverse of `manualScryptDecrypt` and is what we use when
 * creating a new account in this wallet — instead of `pair.toJson(password)`
 * which uses @polkadot/util-crypto's weaker default of N=32768.
 *
 * The point: a JSON exported from this wallet should have the same
 * brute-force resistance as one exported from `wallet.xx.network`. See
 * SECURITY.md (H-1) for the threat-model rationale.
 *
 * `templateJson` provides `address` and `meta` — fields that aren't part
 * of the encryption but are part of the standard keystore JSON shape.
 *
 * Exported for unit testing.
 */
export async function manualScryptEncrypt(
  pkcs8: Uint8Array,
  password: string,
  templateJson: Pick<KeyringPair$Json, 'address' | 'meta'>,
  params: { N: number; r: number; p: number } = {
    N: STRONG_SCRYPT_N,
    r: STRONG_SCRYPT_R,
    p: STRONG_SCRYPT_P,
  }
): Promise<KeyringPair$Json> {
  // Fresh salt + nonce per encryption — never reuse them. tweetnacl's
  // randomBytes uses crypto.getRandomValues under the hood in browsers.
  const salt = nacl.randomBytes(SCRYPT_SALT_LEN);
  const nonce = nacl.randomBytes(NACL_NONCE_LEN);

  // Same key derivation as the decrypt path: 64 bytes from scrypt, first
  // 32 used as the secretbox key.
  const passwordBytes = stringToU8a(password);
  const derivedKey = await scryptAsync(
    passwordBytes,
    salt,
    params.N,
    params.r,
    params.p,
    64
  );
  const secretBoxKey = derivedKey.slice(0, 32);

  // Encrypt the PKCS8 payload with xsalsa20-poly1305.
  const ciphertext = nacl.secretbox(pkcs8, nonce, secretBoxKey);

  // Pack the v3 layout: salt(32) | N(4 LE) | p(4 LE) | r(4 LE) | nonce(24) | ciphertext.
  const encoded = new Uint8Array(NACL_ENCRYPTED_HEADER_LEN + ciphertext.length);
  encoded.set(salt, 0);
  const view = new DataView(encoded.buffer);
  view.setUint32(SCRYPT_SALT_LEN, params.N, true);
  view.setUint32(SCRYPT_SALT_LEN + 4, params.p, true);
  view.setUint32(SCRYPT_SALT_LEN + 8, params.r, true);
  encoded.set(nonce, SCRYPT_HEADER_LEN);
  encoded.set(ciphertext, NACL_ENCRYPTED_HEADER_LEN);

  // Best-effort wipe of the derived key bytes once encryption is done.
  derivedKey.fill(0);
  secretBoxKey.fill(0);

  return {
    address: templateJson.address,
    encoded: base64Encode(encoded),
    encoding: {
      content: ['pkcs8', 'sr25519'],
      type: ['scrypt', 'xsalsa20-poly1305'],
      version: '3',
    },
    meta: templateJson.meta ?? {},
  } as KeyringPair$Json;
}

/**
 * Check that the decrypted PKCS8 payload has the expected header/divider
 * structure. This is the same validation @polkadot/keyring does internally.
 *
 * Exported for unit testing.
 */
export function validatePkcs8(decrypted: Uint8Array): void {
  const header = decrypted.slice(0, PKCS8_HEADER.length);
  for (let i = 0; i < PKCS8_HEADER.length; i++) {
    if (header[i] !== PKCS8_HEADER[i]) {
      throw new Error('Decrypted keystore does not have a valid PKCS8 header.');
    }
  }
}

class XxKeyring {
  private keyring: Keyring | null = null;
  private ready: Promise<void> | null = null;

  /** Wait for WASM crypto to initialize. Must be called before any ops. */
  async init(): Promise<void> {
    if (this.ready) return this.ready;
    this.ready = (async () => {
      await cryptoWaitReady();
      this.keyring = new Keyring({
        type: 'sr25519',
        ss58Format: XX_SS58_PREFIX,
      });
    })();
    return this.ready;
  }

  private ensureReady(): Keyring {
    if (!this.keyring) {
      throw new Error('Keyring not initialized. Call init() first.');
    }
    return this.keyring;
  }

  /** Load all stored accounts from localStorage. */
  listAccounts(): StoredAccount[] {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw) as StoredAccount[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** Check if any account exists (useful to decide onboarding vs dashboard). */
  hasAccounts(): boolean {
    return this.listAccounts().length > 0;
  }

  /** Persist the current list of accounts. */
  private saveAccounts(accounts: StoredAccount[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(accounts));
  }

  /**
   * Generate a new BIP39 mnemonic phrase.
   * The user should back this up BEFORE the account is created.
   */
  generateMnemonic(wordCount: 12 | 24 = 24): string {
    // Words per mnemonic: 12 -> 128 bits entropy, 24 -> 256 bits entropy.
    return mnemonicGenerate(wordCount);
  }

  /** Validate that a mnemonic phrase is well-formed (BIP39). */
  validateMnemonic(mnemonic: string): boolean {
    return mnemonicValidate(mnemonic.trim());
  }

  /**
   * Derive the xx address for a mnemonic WITHOUT persisting an account or
   * touching localStorage. Uses the identical derivation as
   * createFromMnemonic (addFromUri on the ss58-55 keyring), so a phrase
   * backed up here reproduces this exact address when later imported. The
   * in-memory pair is removed immediately.
   *
   * Used for the offline backup signer in two-device-approval setup, whose
   * private key must never live on this device — we only keep its address.
   * Does not go through the manualScryptDecrypt / unlock() path.
   */
  addressFromMnemonic(mnemonic: string): string {
    const keyring = this.ensureReady();
    const trimmed = mnemonic.trim();
    if (!mnemonicValidate(trimmed)) {
      throw new Error('Invalid mnemonic phrase.');
    }
    const pair = keyring.addFromUri(trimmed);
    const { address } = pair;
    keyring.removePair(address);
    return address;
  }

  /**
   * Create a new account from a mnemonic and persist it.
   * The mnemonic should already be validated/confirmed by the user.
   *
   * Implementation note: we use `pair.toJson()` to get the standard PKCS8
   * payload structure, then re-encrypt it at xx-network-strength scrypt
   * (N=131072) instead of @polkadot's weaker N=32768 default. The
   * intermediate decrypted PKCS8 buffer is wiped immediately. See
   * SECURITY.md (H-1) for the rationale.
   */
  async createFromMnemonic(
    mnemonic: string,
    opts: Omit<CreateAccountOptions, 'wordCount'>
  ): Promise<StoredAccount> {
    const keyring = this.ensureReady();
    const trimmed = mnemonic.trim();

    if (!mnemonicValidate(trimmed)) {
      throw new Error('Invalid mnemonic phrase.');
    }

    const pair = keyring.addFromUri(trimmed, { name: opts.name });
    // pair.toJson uses @polkadot's hardcoded N=32768. We use this only as
    // a stepping stone to get the canonical PKCS8 layout, then re-encrypt
    // ourselves with N=131072. The weak JSON is never persisted.
    const weakJson = pair.toJson(opts.password);
    const decrypted = await manualScryptDecrypt(weakJson, opts.password);
    let strongJson: KeyringPair$Json;
    try {
      validatePkcs8(decrypted);
      strongJson = await manualScryptEncrypt(decrypted, opts.password, weakJson);
    } finally {
      // Wipe the intermediate plaintext PKCS8 buffer ASAP.
      decrypted.fill(0);
    }

    // Remove the in-memory pair — we only keep the encrypted JSON.
    keyring.removePair(pair.address);

    const account: StoredAccount = {
      address: pair.address,
      name: opts.name,
      json: strongJson,
      createdAt: Date.now(),
    };

    const accounts = this.listAccounts();
    if (accounts.some((a) => a.address === account.address)) {
      throw new Error('An account with this address already exists.');
    }
    accounts.push(account);
    this.saveAccounts(accounts);

    return account;
  }

  /**
   * Import an account from a Polkadot JSON keystore file.
   *
   * Supports v3 scrypt with custom params (including the N=131072 that
   * wallet.xx.network exports) by manually decrypting instead of relying
   * on @polkadot/keyring's strict default-params-only decrypt.
   *
   * The account is stored with its ORIGINAL encrypted JSON so the user's
   * password continues to work the same way across devices and the main
   * wallet. The unlock() path uses the same manual decrypt.
   */
  async importFromJson(opts: ImportFromJsonOptions): Promise<StoredAccount> {
    const keyring = this.ensureReady();

    // Step 1: Verify the password by manually decrypting with the file's own scrypt params.
    const decrypted = await manualScryptDecrypt(opts.json, opts.password);
    validatePkcs8(decrypted);

    // Step 2: Figure out the address. The JSON already has the SS58-encoded address
    // from the exporting wallet. We re-encode with our SS58 prefix (55) to be safe
    // in case someone imported a non-xx JSON by mistake.
    let address = opts.json.address;
    try {
      const decoded = keyring.decodeAddress(opts.json.address);
      address = keyring.encodeAddress(decoded, XX_SS58_PREFIX);
    } catch {
      // Fall back to the stored address string as-is
    }

    const name = opts.name ?? (opts.json.meta?.name as string) ?? 'Imported';

    const account: StoredAccount = {
      address,
      name,
      json: opts.json,
      createdAt: Date.now(),
    };

    const accounts = this.listAccounts();
    if (accounts.some((a) => a.address === account.address)) {
      throw new Error('An account with this address already exists.');
    }
    accounts.push(account);
    this.saveAccounts(accounts);

    return account;
  }

  /**
   * Add a Ledger-backed account record. Parallel branch to the local
   * keystore path — no key material, no password, no crypto: just the
   * address (as confirmed on the device) and the derivation slots needed
   * to reach the same key again at signing time.
   */
  addLedgerAccount(input: {
    address: string;
    name: string;
    ledger: { account: number; change: number; index: number };
  }): LedgerAccount {
    if (!input.address.startsWith('6')) {
      throw new Error(
        'Not an xx network address — Ledger addresses here start with "6".'
      );
    }
    const { account, change, index } = input.ledger;
    for (const [label, v] of [
      ['account', account],
      ['change', change],
      ['index', index],
    ] as const) {
      if (!Number.isInteger(v) || v < 0) {
        throw new Error(`Invalid derivation slot ${label}: ${v}`);
      }
    }

    const record: LedgerAccount = {
      source: 'ledger',
      address: input.address,
      name: input.name.trim() || 'Ledger account',
      ledger: { account, change, index },
      createdAt: Date.now(),
    };

    const accounts = this.listAccounts();
    if (accounts.some((a) => a.address === record.address)) {
      throw new Error('An account with this address already exists.');
    }
    accounts.push(record);
    this.saveAccounts(accounts);
    return record;
  }

  /** Delete an account by address. */
  removeAccount(address: string): void {
    const accounts = this.listAccounts().filter((a) => a.address !== address);
    this.saveAccounts(accounts);
  }

  /**
   * Rename an account. Updates BOTH the local display name and the keystore
   * JSON's `meta.name` so the new name survives an export → import round-trip
   * to another device.
   */
  renameAccount(address: string, newName: string): void {
    const accounts = this.listAccounts();
    const acct = accounts.find((a) => a.address === address);
    if (!acct) throw new Error('Account not found.');
    acct.name = newName;
    // Ledger records have no keystore JSON to keep in sync — the name is
    // purely local. Only local accounts mirror the name into json.meta.
    if (isLocalAccount(acct)) {
      acct.json = {
        ...acct.json,
        meta: { ...(acct.json.meta || {}), name: newName },
      };
    }
    this.saveAccounts(accounts);
  }

  /**
   * Unlock an account to produce a signing pair.
   *
   * The returned pair holds the decrypted secret key in memory — callers
   * MUST call `pair.lock()` ASAP after signing, and ideally also
   * `xxKeyring.removeFromKeyring(pair.address)` to evict the pair from
   * the in-memory keyring map.
   *
   * Handles both Polkadot-default scrypt params (legacy accounts) and
   * wallet.xx.network custom params (N=131072). We always manually decrypt
   * first, then build the KeyringPair from the raw key material.
   *
   * The intermediate `decrypted` and `secretKey` buffers are zeroed in
   * `finally` blocks. JS Uint8Array.fill is not a guaranteed memory wipe
   * (the engine may have moved the buffer or kept register copies), but
   * it's still meaningfully better than letting the GC decide when —
   * see SECURITY.md (H-2).
   */
  async unlock(address: string, password: string): Promise<KeyringPair> {
    const keyring = this.ensureReady();
    const account = this.listAccounts().find((a) => a.address === address);
    // Same message as a failed decrypt: the unlock path must not
    // reveal whether an address exists in this wallet — an attacker
    // probing with a stolen address list learns nothing either way.
    // Account-not-found here is a caller bug regardless (addresses come
    // from our own store), so no diagnostics are lost in practice.
    if (!account) throw new Error('Incorrect password for this keystore.');
    if (isLedgerAccount(account)) {
      // Defensive — signing flows route Ledger accounts to the device
      // signer before reaching here. If this throws, a caller missed
      // the source branch.
      throw new Error(
        'This is a Ledger account — its key lives on the device and cannot be unlocked here.'
      );
    }

    // Step 1: Manually decrypt to validate the password.
    const decrypted = await manualScryptDecrypt(account.json, password);
    try {
      validatePkcs8(decrypted);

      // Step 2: Extract the secret key and public key from the PKCS8 payload.
      // Layout: [PKCS8_HEADER][64 bytes secretKey][PKCS8_DIVIDER][32 bytes publicKey].
      // Note `slice` returns a copy (not a view), so wiping `decrypted` does
      // not also wipe `secretKey` / `publicKey`.
      const secretStart = PKCS8_HEADER.length;
      const secretKey = decrypted.slice(secretStart, secretStart + 64);
      const dividerStart = secretStart + 64;
      for (let i = 0; i < PKCS8_DIVIDER.length; i++) {
        if (decrypted[dividerStart + i] !== PKCS8_DIVIDER[i]) {
          throw new Error('Decrypted keystore has an invalid PKCS8 divider.');
        }
      }
      const publicKey = decrypted.slice(
        dividerStart + PKCS8_DIVIDER.length,
        dividerStart + PKCS8_DIVIDER.length + 32
      );

      // Step 3: Build a KeyringPair from the raw key material.
      //
      // IMPORTANT: do NOT wipe `secretKey` here. @polkadot/keyring's
      // `addFromPair` retains a reference to (or otherwise depends on)
      // the secretKey buffer for the lifetime of the pair — wiping it
      // immediately after creation collapses the pair's secret state
      // and the next `pair.sign(...)` call fails with
      // "Cannot sign with a locked key pair".
      //
      // The H-2 hardening contract (don't leave plaintext secret material
      // around longer than necessary) is still upheld by:
      //   1. The outer `finally` zeroing the full `decrypted` PKCS8 blob.
      //   2. The mandatory caller-side `pair.lock()` after signing
      //      (documented in this method's docstring; enforced by the
      //      `removeFromKeyring` cleanup pattern in `useTx`).
      // The standalone `secretKey` slice we created above goes out of
      // scope when this function returns and is collectable by the GC;
      // the live secret material lives inside the returned pair, where
      // the caller is responsible for clearing it.
      return keyring.addFromPair(
        { publicKey, secretKey },
        { ...(account.json.meta || {}), name: account.name },
        'sr25519'
      );
    } finally {
      decrypted.fill(0);
    }
  }

  /**
   * Evict an in-memory KeyringPair from @polkadot/keyring's internal map.
   * Callers should invoke this after signing-and-locking is done so the
   * pair object isn't kept alive longer than needed. Safe to call even
   * if the pair isn't in the map (will silently no-op).
   */
  removeFromKeyring(address: string): void {
    try {
      this.keyring?.removePair(address);
    } catch {
      // Ignore — keyring may have already evicted, or pair never existed.
    }
  }

  /**
   * Sign a raw message with an account's key, keeping the unlocked pair's
   * ENTIRE lifetime inside this module (THE RULE facet b, ADR-0003).
   *
   * unlock → sign → lock + evict happen in one call, so no caller ever
   * holds a decrypted-key KeyringPair. Mirrors the `useTx` cleanup
   * discipline: lock() and removeFromKeyring() run in a `finally` block
   * with independent guards so a failure in one cannot skip the other.
   * Ledger accounts reject inside unlock() — there is no local keystore
   * to raw-sign with (their signing is transaction-shaped, on-device).
   *
   * Added for cMix contact-binding signatures (AUDIT-2026-07-002). Any
   * future raw-message signing goes through here — never through a
   * caller-held unlock() outside src/keyring/ or the useTx hooks (the
   * boundary gate flags that pattern).
   */
  async signMessage(
    address: string,
    password: string,
    message: Uint8Array
  ): Promise<Uint8Array> {
    const pair = await this.unlock(address, password);
    try {
      return pair.sign(message);
    } finally {
      if (typeof pair.lock === 'function') {
        try {
          pair.lock();
        } catch {
          /* swallow — eviction below must still run */
        }
      } else {
        // Should not happen with current @polkadot/keyring versions — if it
        // does, the pair is sitting in memory unlocked and we want to know.
        console.error(
          'KeyringPair has no lock() method — possible @polkadot/keyring API change'
        );
      }
      this.removeFromKeyring(address);
    }
  }

  /**
   * Check whether a password can decrypt a stored account's keystore.
   *
   * Used by the batch export flow to validate the user-supplied
   * password(s) BEFORE writing the backup file — a backup the user
   * can't decrypt later is a useless backup, and we'd rather surface
   * that problem now while the user is alert at their machine than
   * six months from now when they actually need to restore.
   *
   * Returns true on a successful decrypt + PKCS8 header check, false
   * on any failure. The decrypted PKCS8 buffer is wiped immediately
   * (per the keyring's "zero secret buffers" security policy) so this
   * is safe to call many times in a tight loop.
   */
  async verifyPassword(address: string, password: string): Promise<boolean> {
    const account = this.listAccounts().find((a) => a.address === address);
    if (!account) return false;
    // Ledger accounts have no password — "no password matches" is the
    // truthful answer. This also keeps the app-lock forgot-PIN recovery
    // loop (which tries the entered password against every account)
    // safely skipping Ledger records.
    if (isLedgerAccount(account)) return false;
    let decrypted: Uint8Array | null = null;
    try {
      decrypted = await manualScryptDecrypt(account.json, password);
      validatePkcs8(decrypted);
      return true;
    } catch {
      return false;
    } finally {
      decrypted?.fill(0);
    }
  }

  /**
   * Export an account's encrypted JSON (for backup or to import into
   * another wallet — including the official xx desktop wallet).
   *
   * The JSON is already encrypted; no password is needed here.
   *
   * Meta enrichment: stored accounts only carry the user-set `name` in
   * their meta (that's all we ever needed locally). The official xx
   * desktop wallet — a polkadot{.js} extension fork — rejects imports
   * whose meta is missing `genesisHash`, treating them as belonging to
   * an unknown network. We pad the four polkadot.js-canonical fields
   * here at export time so the resulting JSON is accepted by any
   * polkadot.js-derived wallet.
   *
   * The spread order means a stored `whenCreated` (if a re-exported
   * file ever carried one) is preserved over the synthesized fallback,
   * and the user-set name is never overwritten by the defaults.
   */
  exportJson(address: string): KeyringPair$Json {
    const account = this.listAccounts().find((a) => a.address === address);
    if (!account) throw new Error('Account not found.');
    if (isLedgerAccount(account)) {
      throw new Error(
        'Ledger accounts have no keystore to export — the key never leaves the device. ' +
          'To use this account elsewhere, connect the Ledger there.'
      );
    }
    return {
      ...account.json,
      meta: {
        genesisHash: XX_GENESIS_HASH,
        isHardware: false,
        tags: [],
        whenCreated: Date.now(),
        ...account.json.meta,
      },
    };
  }
}

export const xxKeyring = new XxKeyring();
